import { AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  State,
  getAllExhibitionsCollection,
  getLanguage,
  getLocalizedImages,
  getOperatorsSettings,
  getSelectedExhibitionId,
  isLoggedInAsAdmin
} from '@app/app.reducer';
import { AppService } from '@app/app.service';
import { LoaderComponent } from '@app/loader/loader.component';
import { LsGridColumnComponent, LsGridComponent, LsGridRowComponent } from '@axess/ng-limestone';
import { BaseRoute, StepRoute } from '@core/data-access/models/routes.model';
import { RouterActions } from '@core/data-access/store/router/router.actions';
import { Store, select } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { HideTranslationPipe } from '@shared/pipes/hide-translation/hide-translation.pipe';
import { GetLocalizedImages } from '@store/customization/customization.actions';
import { LocalizedImagesModel, OperatorsSettingsModel } from '@store/customization/customization.interfaces';
import {
  GetDepartments,
  GetListOfAllExhibitions,
  GetOccupationalGroups,
  GetProfessions,
  GetTitles
} from '@store/exhibition/exhibition.actions';
import { ExhibitionModel } from '@store/exhibition/exhibition.interface';
import { SetEventSeriesPage } from '@store/helpers/helper.actions';
import { AgeRatingComponent } from '@ui/components/age-rating/age-rating.component';
import { ExhibitionCardComponent } from '@ui/components/exhibition-card/exhibition-card.component';
import { cloneDeep, isEmpty } from 'lodash';
import { Observable, Subscription, combineLatest, filter, first } from 'rxjs';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgClass,
    AgeRatingComponent,
    ExhibitionCardComponent,
    LoaderComponent,
    TranslateModule,
    HideTranslationPipe,
    AsyncPipe,
    LsGridComponent,
    LsGridColumnComponent,
    LsGridRowComponent
  ]
})
export class HomeComponent implements OnInit, OnDestroy {
  exhibitions: ExhibitionModel[];
  localizedImages$: Observable<LocalizedImagesModel>;
  isLoggedInAsAdmin$: Observable<boolean>;
  exhibitionId$: Observable<number>;
  exhibitionLanguage$: Observable<string>;
  loaded = false;
  operatorSettings: OperatorsSettingsModel;
  private subscriptions = new Subscription();

  constructor(
    private store: Store<State>,
    private appService: AppService,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef
  ) {
    this.exhibitionId$ = this.store.select(getSelectedExhibitionId);
    this.exhibitionLanguage$ = this.store.select(getLanguage);
  }

  ngOnInit() {
    this.subscriptions.add(
      this.route.queryParams.subscribe((params) => {
        let newUrl = `/${BaseRoute.WebShop}/`;
        const optionalParams = [];

        if (params.Ev) {
          newUrl += `${params.Ev}/${StepRoute.Tickets}`;

          if (params.Code) {
            optionalParams.push(`voucher=${params.Code}`);
          }

          if (params.Lng) {
            optionalParams.push(`lang=${params.Lng}`);
          }

          if (optionalParams.length) {
            newUrl += `?` + optionalParams.join('&');
          }

          window.location.href = window.location.origin + newUrl;
        }
      })
    );

    this.isLoggedInAsAdmin$ = this.store.pipe(select(isLoggedInAsAdmin));
    this.localizedImages$ = this.store.pipe(select(getLocalizedImages));

    // RESET reducers
    this.subscriptions.add(
      this.appService.resetReducers().subscribe(() => {
        this.loaded = true;
        this.cdr.detectChanges();
      })
    );

    // load new settings
    this.subscriptions.add(
      this.store
        .pipe(
          select(getAllExhibitionsCollection),
          filter((allExhibitionsCollection) => !!allExhibitionsCollection.length)
        )
        .subscribe((allExhibitionsCollection) => {
          this.exhibitions = cloneDeep(allExhibitionsCollection).sort((a, b) => {
            const aStartDate = <Date>a.startDate;
            const bStartDate = <Date>b.startDate;

            return aStartDate.getTime() - bStartDate.getTime(); // date to timestamp
          });
        })
    );

    // load new events list
    this.subscriptions.add(
      this.store
        .select(getLanguage)
        .pipe(filter((lang) => !!lang))
        .subscribe((lang) => {
          this.store.dispatch(new GetListOfAllExhibitions(lang));
        })
    );

    //if we come to home page we reset information about event series page
    this.store.dispatch(new SetEventSeriesPage(null));

    this.subscriptions.add(
      combineLatest([this.exhibitionId$, this.exhibitionLanguage$])
        .pipe(filter(([_, exhibitionLanguage]) => !!exhibitionLanguage))
        .subscribe(([exhibitionId, exhibitionLanguage]) => {
          const exhibitionOrOperatorId = exhibitionId ? exhibitionId : -1;

          // effects are no more triggered in this loop so call it in next one
          setTimeout(() => {
            this.store.dispatch(
              new GetLocalizedImages({
                eventId: Number(exhibitionOrOperatorId),
                countryCode: exhibitionLanguage
              })
            );
            this.store.dispatch(new GetTitles(exhibitionOrOperatorId));
            this.store.dispatch(new GetProfessions(exhibitionOrOperatorId));
            this.store.dispatch(new GetDepartments(exhibitionOrOperatorId));
            this.store.dispatch(new GetOccupationalGroups(exhibitionOrOperatorId));
          });
        })
    );

    this.store
      .pipe(
        select(getOperatorsSettings),
        first((operatorSettings) => !!operatorSettings)
      )
      .subscribe((operatorSettings) => {
        this.operatorSettings = operatorSettings;
      });

    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  protected goToUrl($event: MouseEvent, url: string) {
    $event.preventDefault();
    $event.stopPropagation();

    if (isEmpty(url)) {
      return;
    }

    window.open(url, '_blank');
  }

  protected navigateToExhibition($event: MouseEvent, exhibitionId: number) {
    $event.preventDefault();
    $event.stopPropagation();

    this.store.dispatch(RouterActions.navigateToSelectedEvent(exhibitionId));
  }
}
